import * as React from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, UserRound } from 'lucide-react'
import { useClients, useCreateClient } from '@/lib/queries.js'
import { fullName } from '@/lib/utils.js'
import { timeAgo } from '@/lib/format.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, PageHeader } from '@/components/ui/card.jsx'
import { Field, Input } from '@/components/ui/field.jsx'
import { Alert, EmptyState, Spinner } from '@/components/ui/feedback.jsx'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog.jsx'
import { ERR, humanMessage } from '@/lib/errors.js'

const PAGE_SIZE = 50

/**
 * Clients list (Phase 1b screen 3).
 *
 * ── LOAD MORE, NOT PAGE NUMBERS (D10 / C7) ──────────────────────────────────
 * `GET /api/clients` returns rows and no total count, so "page 3 of 12" is not
 * expressible without inventing a number. Rather than fake it, the list loads
 * more on demand — which is also the better interaction on a phone. The named
 * trigger for revisiting is in the plan: the first tenant with enough clients
 * that scrolling stops being reasonable.
 */
export function ClientList () {
  const [search, setSearch] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [limit, setLimit] = React.useState(PAGE_SIZE)
  const [creating, setCreating] = React.useState(false)

  // Debounced so typing a name is one request at the end, not one per keypress.
  React.useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setLimit(PAGE_SIZE) }, 250)
    return () => clearTimeout(t)
  }, [search])

  const { data: clients = [], isPending, isFetching, error } =
    useClients({ search: debounced || undefined, limit })

  // Without a count, a full page is the only signal that more may exist.
  const mayHaveMore = clients.length === limit

  return (
    <>
      <PageHeader
        title="Clients"
        description={
          isPending ? 'Loading…' : `${clients.length}${mayHaveMore ? '+' : ''} ${clients.length === 1 ? 'person' : 'people'}`
        }
        actions={<Button onClick={() => setCreating(true)}><Plus /> New client</Button>}
      />

      <div className="mb-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
        <Input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email or phone"
          aria-label="Search clients"
          className="pl-9"
          data-testid="client-search"
        />
      </div>

      {error && <Alert tone="danger" className="mb-4">{humanMessage(error)}</Alert>}

      {isPending
        ? <div className="flex justify-center py-16"><Spinner label="Loading clients" /></div>
        : clients.length === 0
          ? (debounced
              ? (
                <EmptyState
                  icon={Search}
                  title="No matches"
                  description={`Nothing found for “${debounced}”. Try part of a name, an email address, or a phone number.`}
                />
                )
              : (
                <EmptyState
                  icon={UserRound}
                  title="No clients yet"
                  description="Clients arrive automatically when someone fills in your lead form. You can also add one by hand."
                  action={{ label: 'Add a client', onClick: () => setCreating(true) }}
                  secondaryAction={{ label: 'Set up your lead form', href: '/settings' }}
                />
                ))
          : (
            <>
              <Card className="overflow-hidden">
                <ul className="divide-y divide-line">
                  {clients.map(client => (
                    <li key={client.id}>
                      <Link
                        to={`/clients/${client.id}`}
                        className="flex min-h-tap items-center gap-3 px-4 py-3 hover:bg-surface-sunken"
                        data-testid="client-row"
                      >
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-xs font-semibold text-brand" aria-hidden="true">
                          {(client.first_name?.[0] ?? '?').toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink">
                            {fullName(client)}
                          </span>
                          <span className="block truncate text-sm text-ink-muted">
                            {client.email || client.phone || 'No contact details'}
                          </span>
                        </span>
                        <span className="hidden shrink-0 text-xs text-ink-subtle sm:block">
                          {timeAgo(client.created_at)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>

              {mayHaveMore && (
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="secondary"
                    loading={isFetching}
                    onClick={() => setLimit(l => l + PAGE_SIZE)}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
            )}

      <NewClientDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}

/**
 * Create a client.
 *
 * `last_name` is deliberately optional (Phase 1 D3): contractor lead forms
 * collect one name field, and splitting on whitespace invents data. The form
 * mirrors the schema rather than demanding more than the API does.
 *
 * The API requires an email or a phone — one of the two, not both — and answers
 * 400 `missing_contact`. That is stated up front rather than discovered.
 */
export function NewClientDialog ({ open, onOpenChange, onCreated }) {
  const createClient = useCreateClient()
  const [form, setForm] = React.useState({ first_name: '', last_name: '', email: '', phone: '' })
  const [error, setError] = React.useState(null)

  React.useEffect(() => {
    if (open) {
      setForm({ first_name: '', last_name: '', email: '', phone: '' })
      setError(null)
    }
  }, [open])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function handleSubmit (event) {
    event.preventDefault()
    setError(null)
    try {
      const { client } = await createClient.mutateAsync({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined
      })
      onOpenChange(false)
      onCreated?.(client)
    } catch (err) {
      setError(err)
    }
  }

  const contactMissing = !form.email.trim() && !form.phone.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New client">
        <form id="client-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && (
            <Alert tone="danger">
              {error.slug === ERR.MISSING_CONTACT
                ? 'Add an email address or a phone number so this person can be contacted.'
                : humanMessage(error)}
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="client-first" label="First name" value={form.first_name}
              onChange={e => set('first_name', e.target.value)} required autoComplete="off"
            />
            <Field
              id="client-last" label="Last name" value={form.last_name}
              onChange={e => set('last_name', e.target.value)}
              hint="Optional" autoComplete="off"
            />
          </div>

          <Field
            id="client-email" label="Email" type="email" value={form.email}
            onChange={e => set('email', e.target.value)}
            autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
          />
          <Field
            id="client-phone" label="Phone" type="tel" value={form.phone}
            onChange={e => set('phone', e.target.value)} inputMode="tel"
            hint={contactMissing ? 'An email address or a phone number is required.' : undefined}
          />
        </form>

        <DialogFooter className="-mx-5 -mb-4 mt-4">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="submit" form="client-form" loading={createClient.isPending}
            disabled={!form.first_name.trim() || contactMissing}
          >
            Add client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
